import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { DynamicFormRenderer } from './DynamicFormRenderer';
import {
  useFormDefinitionFull,
  useCreateSection,
  useUpdateSection,
  useDeleteSection,
  useCreateField,
  useUpdateField,
  useDeleteField,
  useFieldLibrary,
  formBuilderKeys,
  type FormDefinitionFull,
  type FormSection,
  type FormField,
  type RenderAs,
} from './hooks/useFormBuilder';

const RENDER_OPTIONS: Array<{ value: RenderAs; label: string }> = [
  { value: 'section', label: 'Card Section' },
  { value: 'tab', label: 'Tab' },
  { value: 'accordion', label: 'Accordion' },
];

/**
 * FormEditorPage — edit a form definition: manage sections and attach fields from the library.
 */
export function FormEditorPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: form, isLoading } = useFormDefinitionFull(formId ?? '');
  const createSectionMutation = useCreateSection(formId ?? '');
  const updateSectionMutation = useUpdateSection(formId ?? '');
  const deleteSectionMutation = useDeleteSection(formId ?? '');

  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Validate on blur so the preview demonstrates field formats (regex) live.
  const previewMethods = useForm({ mode: 'onBlur' });
  const [attachingToSection, setAttachingToSection] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{ sectionId: string; field: FormField } | null>(
    null,
  );
  const [editingSection, setEditingSection] = useState<FormSection | null>(null);

  const handleAddSection = (data: { name: string; render_as: RenderAs }) => {
    const nextOrder = form?.sections?.length ?? 0;
    createSectionMutation.mutate(
      { ...data, display_order: nextOrder },
      {
        onSuccess: () => {
          setAddSectionOpen(false);
          toast({ variant: 'success', title: 'Section added' });
        },
      },
    );
  };

  const handleDeleteSection = (sectionId: string, name: string) => {
    if (confirm(`Delete section "${name}" and all its fields?`)) {
      deleteSectionMutation.mutate(sectionId, {
        onSuccess: () => toast({ variant: 'success', title: 'Section deleted' }),
      });
    }
  };

  const handleMoveField = async (sectionId: string, fieldId: string, direction: 'up' | 'down') => {
    if (!form || !formId) return;
    const section = form.sections.find((s) => s.id === sectionId);
    if (!section) return;

    // Order fields exactly as the list renders them. The API returns fields
    // sorted by display_order; we mirror that here with a stable id tiebreak so
    // this logic stays consistent with the rendered order even when several
    // fields share the same display_order (e.g. freshly attached fields that
    // all defaulted to the same value). Swapping two equal display_order values
    // would be a no-op — which is exactly why the last field "wouldn't move".
    const fields = [...section.fields].sort(
      (a, b) => a.display_order - b.display_order || a.id.localeCompare(b.id),
    );
    const idx = fields.findIndex((f) => f.id === fieldId);
    if (idx < 0) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === fields.length - 1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;

    // Reorder the array, then renumber every field to a clean 0..n-1 sequence.
    // This is robust to duplicate/tied display_order values, where a plain
    // value swap would write the same number back to both rows and move nothing.
    [fields[idx], fields[swapIdx]] = [fields[swapIdx], fields[idx]];

    // Persist only the rows whose display_order actually changes.
    const updates = fields
      .map((f, order) => ({ f, order }))
      .filter(({ f, order }) => f.display_order !== order)
      .map(({ f, order }) =>
        apiClient.put(`/form-builder/${formId}/sections/${sectionId}/fields/${f.id}`, {
          display_order: order,
        }),
      );

    if (updates.length > 0) {
      await Promise.all(updates);
      queryClient.invalidateQueries({ queryKey: formBuilderKeys.detail(formId) });
    }
  };

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/settings/form-builder')}
            className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 mb-1"
          >
            &larr; Back to Forms
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{form.name}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>{form.description || 'No description'}</span>
            <span>&middot;</span>
            <select
              value={form.entity_scope}
              onChange={async (e) => {
                try {
                  await apiClient.put(`/form-builder/${form.id}`, { entity_scope: e.target.value });
                  queryClient.invalidateQueries({ queryKey: formBuilderKeys.detail(form.id) });
                  toast({ variant: 'success', title: 'Entity scope updated' });
                } catch {
                  toast({ variant: 'error', title: 'Failed to update scope' });
                }
              }}
              className="rounded border border-border bg-transparent px-2 py-0.5 text-sm dark:border-border-dark"
            >
              <option value="contact">Contact</option>
              <option value="job">Job</option>
              <option value="invoice">Invoice</option>
              <option value="document">Document</option>
              <option value="time_entry">Time Entry</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            Preview
          </Button>
          <Button onClick={() => setAddSectionOpen(true)}>Add Section</Button>
        </div>
      </div>

      {/* Sections */}
      {form.sections.length === 0 ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-sm text-gray-500">
              No sections yet. Add a section to start building your form.
            </p>
          </CardContent>
        </Card>
      ) : (
        form.sections.map((section) => (
          <Card key={section.id}>
            <CardHeader
              title={section.name}
              description={`${section.fields.length} field${section.fields.length !== 1 ? 's' : ''} · Render: ${section.render_as}`}
              action={
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAttachingToSection(section.id)}
                  >
                    Attach Field
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingSection(section)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteSection(section.id, section.name)}
                  >
                    Delete
                  </Button>
                </div>
              }
            />
            <CardContent>
              {section.fields.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">
                  No fields in this section. Click "Attach Field" to add from the library.
                </p>
              ) : (
                <div className="divide-y divide-border dark:divide-border-dark">
                  {[...section.fields]
                    .sort((a, b) => a.display_order - b.display_order || a.id.localeCompare(b.id))
                    .map((field, fieldIdx, sortedFields) => (
                      <div key={field.id} className="flex items-center justify-between py-2.5">
                        <div className="flex items-center gap-3">
                          {/* Reorder arrows */}
                          <div className="flex flex-col gap-0.5">
                            <button
                              type="button"
                              onClick={() => handleMoveField(section.id, field.id, 'up')}
                              disabled={fieldIdx === 0}
                              className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed dark:hover:text-gray-200"
                              aria-label="Move up"
                            >
                              <svg
                                className="h-3.5 w-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 15l7-7 7 7"
                                />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveField(section.id, field.id, 'down')}
                              disabled={fieldIdx === sortedFields.length - 1}
                              className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed dark:hover:text-gray-200"
                              aria-label="Move down"
                            >
                              <svg
                                className="h-3.5 w-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {field.label}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">({field.field_key})</span>
                          </div>
                          <Badge variant="default" size="sm">
                            {field.field_type}
                          </Badge>
                          {field.is_required && (
                            <Badge variant="warning" size="sm">
                              Required
                            </Badge>
                          )}
                          {field.is_system_field && (
                            <Badge variant="info" size="sm">
                              System
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingField({ sectionId: section.id, field })}
                        >
                          Edit
                        </Button>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Add Section Modal */}
      {addSectionOpen && (
        <AddSectionModal
          onSave={handleAddSection}
          onClose={() => setAddSectionOpen(false)}
          isSaving={createSectionMutation.isPending}
        />
      )}

      {/* Attach Field from Library Modal */}
      {attachingToSection && formId && (
        <AttachFieldModal
          formId={formId}
          sectionId={attachingToSection}
          existingFieldKeys={form.sections.flatMap((s) => s.fields.map((f) => f.field_key))}
          onClose={() => setAttachingToSection(null)}
        />
      )}

      {/* Edit Field (label + default value override) */}
      {editingField && formId && (
        <EditFieldModal
          formId={formId}
          sectionId={editingField.sectionId}
          field={editingField.field}
          onClose={() => setEditingField(null)}
        />
      )}

      {/* Edit Section */}
      {editingSection && formId && (
        <EditSectionModal
          formId={formId}
          section={editingSection}
          isSaving={updateSectionMutation.isPending}
          onSave={(data) => {
            updateSectionMutation.mutate(
              { sectionId: editingSection.id, data },
              {
                onSuccess: () => {
                  setEditingSection(null);
                  toast({ variant: 'success', title: 'Section updated' });
                },
              },
            );
          }}
          onClose={() => setEditingSection(null)}
        />
      )}

      {/* Form Preview Modal */}
      {showPreview && form && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Form Preview: {form.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This is how the form will appear to users
                </p>
              </div>
              <Button type="button" variant="outline" onClick={() => setShowPreview(false)}>
                Close
              </Button>
            </div>
            <FormProvider {...previewMethods}>
              <FormPreviewContent formDefinition={form} />
            </FormProvider>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add Section Modal ───────────────────────────────────────────────────────

function AddSectionModal({
  onSave,
  onClose,
  isSaving,
}: {
  onSave: (data: { name: string; render_as: RenderAs }) => void;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState('');
  const [renderAs, setRenderAs] = useState<RenderAs>('section');

  return (
    <Modal open onClose={onClose} title="Add Section" size="sm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ name, render_as: renderAs });
        }}
        className="space-y-4"
      >
        <Input
          label="Section Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Tax Information"
        />
        <Select
          label="Render As"
          value={renderAs}
          onChange={(e) => setRenderAs(e.target.value as RenderAs)}
          options={RENDER_OPTIONS}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!name.trim()}>
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Attach Field from Library Modal ─────────────────────────────────────────

function AttachFieldModal({
  formId,
  sectionId,
  existingFieldKeys,
  onClose,
}: {
  formId: string;
  sectionId: string;
  existingFieldKeys: string[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { data: library, isLoading: libraryLoading } = useFieldLibrary();
  const createMutation = useCreateField(formId, sectionId);

  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [labelOverride, setLabelOverride] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [hintText, setHintText] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(existingFieldKeys.length);

  // Filter out fields already on this form
  const availableFields = (library ?? []).filter((f) => !existingFieldKeys.includes(f.field_key));

  const selectedLibraryField = availableFields.find((f) => f.id === selectedFieldId);

  const handleAttach = () => {
    if (!selectedLibraryField) return;

    const payload: Record<string, unknown> = {
      field_key: selectedLibraryField.field_key,
      label: labelOverride.trim() || selectedLibraryField.label,
      field_type: selectedLibraryField.field_type,
      is_required: isRequired,
      display_order: displayOrder,
      placeholder: selectedLibraryField.placeholder,
      hint_text: hintText.trim() || selectedLibraryField.hint_text || null,
      default_value: defaultValue.trim() || selectedLibraryField.default_value || null,
      width: selectedLibraryField.width,
      options: selectedLibraryField.options ?? [],
      validation_rules: selectedLibraryField.validation_rules ?? {},
      is_system_field: selectedLibraryField.is_system_field,
      db_column: selectedLibraryField.db_column,
      lookup_category: selectedLibraryField.lookup_category,
      field_library_id: selectedLibraryField.id,
      label_override: labelOverride.trim() || null,
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: `"${labelOverride.trim() || selectedLibraryField.label}" attached`,
        });
        onClose();
      },
    });
  };

  return (
    <Modal open onClose={onClose} title="Attach Field from Library" size="md">
      <div className="space-y-4">
        {libraryLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            <span className="ml-2 text-sm text-gray-500">Loading field library...</span>
          </div>
        ) : availableFields.length === 0 ? (
          <div className="py-4 text-center">
            <p className="text-sm text-gray-500">
              All fields from the library are already attached to this form.
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Create new custom fields in the Field Library first.
            </p>
          </div>
        ) : (
          <>
            <Select
              label={`Select Field (${availableFields.length} available)`}
              value={selectedFieldId}
              onChange={(e) => {
                setSelectedFieldId(e.target.value);
                setLabelOverride('');
                setDefaultValue('');
                setHintText('');
                setIsRequired(false);
                setDisplayOrder(existingFieldKeys.length);
              }}
              options={availableFields.map((f) => ({
                value: f.id,
                label: `${f.label} (${f.field_key}) — ${f.field_type}${f.is_system_field ? ' [System]' : ''}`,
              }))}
              placeholder="Choose a field from the library..."
            />

            {selectedLibraryField && (
              <>
                <div className="rounded-lg border border-border bg-surface-secondary p-3 dark:border-border-dark dark:bg-surface-dark-secondary">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {selectedLibraryField.label}
                    </span>
                    <Badge
                      variant={selectedLibraryField.is_system_field ? 'info' : 'default'}
                      size="sm"
                    >
                      {selectedLibraryField.is_system_field ? 'System' : 'Custom'}
                    </Badge>
                  </div>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                    <dt className="text-gray-500 dark:text-gray-400">Type</dt>
                    <dd className="text-gray-900 dark:text-gray-100">
                      {selectedLibraryField.field_type}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Key</dt>
                    <dd className="font-mono text-gray-900 dark:text-gray-100">
                      {selectedLibraryField.field_key}
                    </dd>
                    {selectedLibraryField.is_system_field && selectedLibraryField.db_column && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">DB Column</dt>
                        <dd className="font-mono text-gray-900 dark:text-gray-100">
                          {selectedLibraryField.db_column}
                        </dd>
                      </>
                    )}
                    {selectedLibraryField.lookup_category && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">Lookup</dt>
                        <dd className="text-gray-900 dark:text-gray-100">
                          {selectedLibraryField.lookup_category}
                        </dd>
                      </>
                    )}
                    {selectedLibraryField.description && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">Description</dt>
                        <dd className="text-gray-900 dark:text-gray-100">
                          {selectedLibraryField.description}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>

                <Input
                  label="Label Override (optional)"
                  value={labelOverride}
                  onChange={(e) => setLabelOverride(e.target.value)}
                  placeholder={selectedLibraryField.label}
                  hint="Leave blank to use the library's default label"
                />

                <Input
                  label="Default Value (optional)"
                  value={defaultValue}
                  onChange={(e) => setDefaultValue(e.target.value)}
                  placeholder={selectedLibraryField.default_value || 'No default'}
                  hint="Override the default value for this form placement"
                />

                <Input
                  label="Hint Text (optional)"
                  value={hintText}
                  onChange={(e) => setHintText(e.target.value)}
                  placeholder={selectedLibraryField.hint_text || 'No hint'}
                  hint="Help text shown below the field"
                />

                <div className="grid grid-cols-2 items-end gap-4">
                  <Input
                    label="Display Order"
                    type="number"
                    value={String(displayOrder)}
                    onChange={(e) => setDisplayOrder(Number(e.target.value))}
                    hint="Position of this field within the section"
                  />
                  <label
                    htmlFor="attach-required"
                    className="flex h-[42px] cursor-pointer items-center gap-2 rounded-lg border border-border px-3 dark:border-border-dark"
                  >
                    <input
                      type="checkbox"
                      id="attach-required"
                      checked={isRequired}
                      onChange={(e) => setIsRequired(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Required field</span>
                  </label>
                </div>
              </>
            )}
          </>
        )}

        <div className="flex justify-end gap-3 border-t border-border pt-4 dark:border-border-dark">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAttach}
            loading={createMutation.isPending}
            disabled={!selectedFieldId}
          >
            Attach Field
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Edit Field Modal (label + default value only) ───────────────────────────

function EditFieldModal({
  formId,
  sectionId,
  field,
  onClose,
}: {
  formId: string;
  sectionId: string;
  field: FormField;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const updateMutation = useUpdateField(formId, sectionId);
  const deleteMutation = useDeleteField(formId, sectionId);

  const [label, setLabel] = useState(field.label);
  const [defaultValue, setDefaultValue] = useState(field.default_value ?? '');
  const [hintText, setHintText] = useState(field.hint_text ?? '');
  const [isRequired, setIsRequired] = useState(field.is_required);
  const [displayOrder, setDisplayOrder] = useState(field.display_order);

  const handleSave = () => {
    updateMutation.mutate(
      {
        fieldId: field.id,
        data: {
          label,
          default_value: defaultValue || null,
          hint_text: hintText || null,
          is_required: isRequired,
          display_order: displayOrder,
          label_override: label !== field.label ? label : null,
        },
      },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Field updated' });
          onClose();
        },
      },
    );
  };

  const handleRemove = () => {
    if (confirm(`Remove "${field.label}" from this form?`)) {
      deleteMutation.mutate(field.id, {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Field removed' });
          onClose();
        },
      });
    }
  };

  return (
    <Modal open onClose={onClose} title={`Edit: ${field.label}`} size="md">
      <div className="space-y-4">
        {/* Read-only info */}
        <div className="rounded-lg bg-gray-50 dark:bg-surface-dark-secondary p-3 text-sm space-y-1">
          <p>
            <strong>Field Key:</strong> {field.field_key}
          </p>
          <p>
            <strong>Type:</strong> {field.field_type}
          </p>
          {field.is_system_field && (
            <p>
              <strong>DB Column:</strong> {field.db_column}
            </p>
          )}
          {field.lookup_category && (
            <p>
              <strong>Lookup:</strong> {field.lookup_category}
            </p>
          )}
        </div>

        {/* Editable properties */}
        <Input
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
          hint="Display label on the form (can differ from library default)"
        />

        <Input
          label="Default Value"
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
          placeholder="No default"
        />

        <Input
          label="Hint Text"
          value={hintText}
          onChange={(e) => setHintText(e.target.value)}
          placeholder="Help text shown below the field"
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Display Order"
            type="number"
            value={String(displayOrder)}
            onChange={(e) => setDisplayOrder(Number(e.target.value))}
          />
          <div className="flex items-center gap-2 pt-6">
            <input
              type="checkbox"
              id="edit-required"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600"
            />
            <label htmlFor="edit-required" className="text-sm text-gray-700 dark:text-gray-300">
              Required
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border dark:border-border-dark">
          <Button
            type="button"
            variant="ghost"
            onClick={handleRemove}
            loading={deleteMutation.isPending}
            className="text-red-600 hover:text-red-700"
          >
            Remove from Form
          </Button>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              loading={updateMutation.isPending}
              disabled={!label.trim()}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Edit Section Modal ──────────────────────────────────────────────────────

function EditSectionModal({
  section,
  isSaving,
  onSave,
  onClose,
}: {
  formId: string;
  section: FormSection;
  isSaving: boolean;
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(section.name);
  const [description, setDescription] = useState(section.description ?? '');
  const [renderAs, setRenderAs] = useState<RenderAs>(section.render_as);

  return (
    <Modal open onClose={onClose} title={`Edit Section: ${section.name}`} size="sm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ name, description: description || null, render_as: renderAs });
        }}
        className="space-y-4"
      >
        <Input
          label="Section Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Personal Information"
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
        />
        <Select
          label="Render As"
          value={renderAs}
          onChange={(e) => setRenderAs(e.target.value as RenderAs)}
          options={RENDER_OPTIONS}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!name.trim()}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Form Preview with Tabs ──────────────────────────────────────────────────

function FormPreviewContent({ formDefinition }: { formDefinition: FormDefinitionFull }) {
  const sections = formDefinition.sections ?? [];
  const tabSections = sections.filter((s) => s.render_as === 'tab' && !s.parent_tab_id);
  const orphanSections = sections.filter(
    (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
  );

  const firstTabId = tabSections.length > 0 ? `section-${tabSections[0].id}` : 'details';
  const { activeTab, onChange } = useTabs(firstTabId);

  // Build tabs array
  const tabs =
    tabSections.length > 0
      ? tabSections.map((s) => ({ id: `section-${s.id}`, label: s.name }))
      : orphanSections.length > 0
        ? [{ id: 'details', label: 'Details' }]
        : [];

  if (tabs.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="py-8 text-center text-sm text-gray-500">No sections to preview.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs tabs={tabs} activeTab={activeTab} onChange={onChange} />

      {/* Tab Panels */}
      {tabSections.map((tab, tabIndex) => {
        // Explicit children: sections with parent_tab_id matching this tab
        const explicitChildren = sections
          .filter((s) => s.parent_tab_id === tab.id && s.render_as !== 'tab' && s.is_active)
          .sort((a, b) => a.display_order - b.display_order);

        // Implicit children: if no explicit children, assign orphans by display_order proximity
        let implicitChildren: typeof sections = [];
        if (explicitChildren.length === 0) {
          const nextTab = tabSections[tabIndex + 1];
          implicitChildren = orphanSections.filter((s) => {
            const afterThisTab = s.display_order >= tab.display_order;
            const beforeNextTab = nextTab ? s.display_order < nextTab.display_order : true;
            return afterThisTab && beforeNextTab;
          });
        }

        const children = explicitChildren.length > 0 ? explicitChildren : implicitChildren;
        const tabHasFields = tab.fields.filter((f) => f.is_active).length > 0;

        const sectionsToRender = [
          ...(tabHasFields ? [{ ...tab, render_as: 'section' as const }] : []),
          ...children,
        ];

        return (
          <TabPanel key={tab.id} tabId={`section-${tab.id}`} activeTab={activeTab}>
            <DynamicFormRenderer
              formDefinition={{ ...formDefinition, sections: sectionsToRender }}
            />
          </TabPanel>
        );
      })}

      {/* Orphan sections (no tabs) */}
      {tabSections.length === 0 && orphanSections.length > 0 && (
        <TabPanel tabId="details" activeTab={activeTab}>
          <DynamicFormRenderer formDefinition={{ ...formDefinition, sections: orphanSections }} />
        </TabPanel>
      )}
    </div>
  );
}
